import { useCurrentPath } from "@/hooks";
import { useLocaleStore } from "@/stores";
import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Configure, useInstantSearch, useMenu, useSearchBox } from "react-instantsearch";
import { useNavigate, useParams } from "react-router-dom";
import {
    SearchOptionDropdown,
    SearchOptionDropdownLabel,
    SearchOptionDropdownOption,
} from "./components/SearchOptionDropdown";

const searchOptions = [
    { id: 0, value: "0", key: "objects" },
    { id: 1, value: "1", key: "documentation" },
];

const Search = () => {
    const activeRegion = useLocaleStore((state) => state.activeRegion);
    const activeLanguage = useLocaleStore((state) => state.activeLanguage);

    const [selected, setSelected] = useState(searchOptions[0]);
    const lastRefinedTypeValue = useRef<string | null>(null);

    const { refine } = useMenu({ attribute: "type" });
    const { query, refine: refineSearchBox } = useSearchBox();
    const { status } = useInstantSearch();

    const [inputValue, setInputValue] = useState(query);
    const inputRef = useRef<HTMLInputElement>(null);

    const params = useParams();
    const currentPath = useCurrentPath();

    const navigate = useNavigate();

    const isSearchStalled = status === "stalled";

    useEffect(() => {
        if (lastRefinedTypeValue.current === selected.value) return;
        lastRefinedTypeValue.current = selected.value;
        refine(selected.value);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected]);

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();

        refineSearchBox(inputValue);

        if (currentPath !== "/:locale/search")
            navigate(`/${params.locale}/search`, {
                replace: true,
            });

        if (inputRef.current) {
            inputRef.current.blur();
        }
    };

    const onReset = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();

        onClear();
    };

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(event.target.value);
    };

    const onClear = () => {
        setInputValue("");
        refineSearchBox("");
    };

    const { t } = useTranslation(["header"]);

    // Guard against ever sending a malformed "langCode:null-null" filter (which matches nothing) if this
    // ever renders before the locale store is resolved - only apply the filter once both are set.
    const localeReady = Boolean(activeRegion) && Boolean(activeLanguage);

    return (
        <form className="flex flex-auto" onSubmit={onSubmit} onReset={onReset}>
            {localeReady && <Configure filters={`langCode:${activeRegion}-${activeLanguage}`} />}

            <label htmlFor="search-query" className="sr-only">
                {t("search-bar.button", { ns: "header" })}
            </label>
            <div className="flex grow">
                <div className="relative flex flex-auto rounded-l-full bg-white border border-r-0 -outline-offset-1 border-[#E6E8EC] h-12">
                    <SearchOptionDropdown value={selected} onChange={setSelected}>
                        {searchOptions.map((value) => (
                            <SearchOptionDropdownOption key={value.id} value={value}>
                                <SearchOptionDropdownLabel>
                                    {t(`search-bar.type.${value.key}`, { ns: "header" })}
                                </SearchOptionDropdownLabel>
                            </SearchOptionDropdownOption>
                        ))}
                    </SearchOptionDropdown>
                    <input
                        id="search-query"
                        name="search-query"
                        type="text"
                        value={inputValue}
                        onChange={onChange}
                        autoCapitalize="off"
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck="false"
                        autoFocus={false}
                        placeholder={t(`search-bar.placeholder.${selected.key}`, { ns: "header" })}
                        className="block min-w-0 grow py-1.5 pl-3 pr-6 text-base text-gray-900 placeholder:text-gray-400 focus:outline-none"
                    />
                </div>
                <button
                    type="submit"
                    className="flex shrink-0 items-center gap-x-1.5 rounded-r-full bg-primary-500 pl-4 pr-5 py-2 text-white hover:bg-primary-600 cursor-pointer font-medium"
                    disabled={isSearchStalled || inputValue.trim().length === 0 || !localeReady}
                >
                    <MagnifyingGlassIcon
                        aria-hidden="true"
                        className="pointer-events-none col-start-1 row-start-1 size-6 self-center justify-self-end"
                    />
                    {t("search-bar.button", { ns: "header" })}
                </button>
            </div>
        </form>
    );
};

export default Search;
